import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📗',
  number: 'Beginner & Gentle Yoga — 20 Classes',
  title: 'Beginner & Gentle Yoga',
  subtitle: 'The foundation your students need to feel safe and supported from day one',
  problem: 'Teaching beginners is one of the most rewarding — and most demanding — parts of being a yoga teacher. This block gives you everything you need to guide new students with clarity, patience, and structure, without improvising a single cue.',
  bullets: ['How to introduce foundational poses in a logical, accessible progression', 'How to guide students who have never practiced yoga before', 'How to create a safe, welcoming environment through clear language and pacing', 'Modifications, prop options, and safer alternatives built into every class'],
  result: 'Your beginners feel supported, capable, and motivated to come back — from their very first class.',
  popular: true
}, {
  icon: '📘',
  number: 'Hatha Yoga — 20 Classes',
  title: 'Hatha Yoga',
  subtitle: 'Structured, balanced, and ready to teach at any level',
  problem: 'Hatha classes are among the most requested — and the hardest to keep fresh week after week. This block gives you 20 fully structured sessions with varied focus areas, so every class feels intentional and complete.',
  bullets: ['How to build balanced Hatha sessions that flow naturally', 'How to layer breathwork, alignment, and awareness without losing the class', 'How to keep long-term students engaged with genuine variety', 'Teaching cues and transitions written and ready for every pose'],
  result: 'You teach Hatha with confidence and consistency — and your students feel the structure without it ever feeling rigid.'
}, {
  icon: '📙',
  number: 'Slow Flow Yoga — 20 Classes',
  title: 'Slow Flow Yoga',
  subtitle: 'Movement with intention — fully guided from the first pose to the final breath',
  problem: 'Slow Flow demands precise cueing, smooth transitions, and a clear sense of pace. This block removes the planning burden entirely and gives you sessions that move beautifully from the first moment.',
  bullets: ['How to build Slow Flow sequences that connect breath and movement seamlessly', 'How to cue transitions so the class feels like one continuous experience', 'How to maintain energy and awareness without rushing or losing momentum', 'Complete timing so you never run short or go overtime'],
  result: 'Your Slow Flow classes feel polished, intentional, and deeply satisfying for your students — every single time.'
}, {
  icon: '📕',
  number: 'Mobility Yoga — 15 Classes',
  title: 'Mobility Yoga',
  subtitle: 'The sessions your students need most — and the ones hardest to structure well',
  problem: 'Mobility work requires a specific kind of sequencing that most general yoga resources do not provide. This block gives you targeted classes built around functional movement, so your students actually feel the difference.',
  bullets: ['How to structure mobility sessions that address real movement restrictions', 'How to explain the purpose of each pose so students stay engaged', 'How to progress mobility work safely across multiple sessions', 'Modifications for students with limited range of movement'],
  result: 'Your students leave mobility classes feeling genuinely more free in their bodies — and they notice the difference.'
}, {
  icon: '📓',
  number: 'Restorative Yoga — 15 Classes',
  title: 'Restorative Yoga',
  subtitle: 'Deep rest, fully guided — without a single moment of improvisation',
  problem: 'Restorative classes are deceptively demanding to teach well. The pacing, the language, the silence — all of it requires preparation. This block gives you everything structured so you can focus entirely on holding the space.',
  bullets: ['How to guide restorative sessions with calm, confident language', 'How to sequence poses for progressive release and comfort', 'How to use props effectively and explain their purpose clearly', 'Opening and closing scripts that set the tone for genuine rest'],
  result: 'Your restorative classes become the sessions your students look forward to most — and the ones they tell their friends about.'
}, {
  icon: '📔',
  number: 'Chair, Breath, Yin-Inspired & Themed — 30 Classes',
  title: 'Chair, Breath, Yin & Themed Classes',
  subtitle: 'Versatility across every context, student, and environment',
  problem: 'These four categories cover the teaching moments most yoga teachers are least prepared for — adaptive students, breath-focused sessions, Yin-inspired work, and themed classes for special occasions. This block ensures you are never caught without the right session for the room.',
  bullets: ['Chair Yoga classes built for students with limited mobility or seated practice needs', 'Breath & Relaxation classes for stress relief, calming, and nervous system support', 'Yin-Inspired sessions for longer holds, deeper release, and quiet focus', 'Themed classes for seasons, intentions, or special teaching moments'],
  result: 'You have adaptable options for a wider range of students, teaching environments, and mobility needs — without building anything from scratch.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-black" data-id="element-281">
      <div className="max-w-6xl mx-auto" data-id="element-282">
        <div className="text-center mb-16" data-id="element-283">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-284">
            📚 120 Ready-to-Teach Yoga Classes
          </h2>
          <p className="text-xl text-gold-light" data-id="element-285">
            9 Categories · 40+ Weeks of Fresh Content · Nothing to Build From
            Scratch
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-286">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-dark-card rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-dark-border'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-287">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-black font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-288">
                  MOST POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-289">
                <span className="text-4xl" data-id="element-290">{tomo.icon}</span>
                <div data-id="element-291">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-292">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1" data-id="element-293">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-white font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-294">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-295">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-296">
                  What does it solve in your teaching?
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-297">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-298">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-299">
                    <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" data-id="element-300" />
                    <span className="text-gray-200 text-sm" data-id="element-301">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-auto" data-id="element-302">
                <span className="text-gold font-bold block mb-1" data-id="element-303">Result:</span>
                <p className="text-white text-sm" data-id="element-304">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}